import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import HTMLWrapper from '@comp/Question/HTMLWrapper';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { setCurrentSelectedAnswer } from '@actions/quizTaking';
import {
  getCurrentQuestionInfo,
  getCurrentSelectedAnswer,
  getCurrentQuestion,
  getQuizQuestionAnswers
} from '@selectors/quizTaking';
import isEmptyUtil from '@utils/isEmpty';
import { QUESTION_TYPE_MAP } from '@constants/quiz';
import useMaxAttemptQuestions from '@cont/Quiz/Taking/Common/useMaxAttemptQuestions';
import Dangerous from '@educator/Container/AutoDPP/DangerousContent';
import useQuestionStatus from '@cont/Quiz/RevampedTaking/Navigation/useQuestionStatus';
import { NTA_COLORS } from '../constants';
import { ntaPageCss, ntaQuestionContentCss } from '../typography';
import NtaOptionControl from './NtaOptionControl';
import NtaIntegerAnswer from './NtaIntegerAnswer';

const OptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $hasComprehension }) => ($hasComprehension ? '8px' : '24px')};
  align-items: flex-start;
  padding: 8px 0 0 16px;
  ${ntaPageCss}
`;

const OptionItem = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  color: ${NTA_COLORS.questionText};
  ${ntaQuestionContentCss}
  width: ${({ $hasComprehension }) => ($hasComprehension ? '100%' : 'auto')};

  &:focus-visible {
    outline: 2px solid ${NTA_COLORS.accentBlue};
    outline-offset: 2px;
  }
`;

const OptionContent = styled.span`
  flex: 1;
  min-width: 0;
`;

const NtaOptions = ({ code }) => {
  const dispatch = useDispatch();

  const currentQuestion = useSelectorWithShallowEqual(getCurrentQuestion);
  const { sectionUID, questionUID } = useSelectorWithShallowEqual(
    getCurrentQuestionInfo
  );
  const answers = useSelectorWithShallowEqual(
    getQuizQuestionAnswers(sectionUID, questionUID)
  );
  const currentSelectedAnswer = useSelectorWithShallowEqual(
    getCurrentSelectedAnswer
  );

  const { isAttempted } = useQuestionStatus(sectionUID, questionUID);
  const { isQuestionAttemptable } = useMaxAttemptQuestions(sectionUID);

  useEffect(() => {
    dispatch(setCurrentSelectedAnswer([...answers]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  const isOptionDisabled = () => !isAttempted && !isQuestionAttemptable;

  const handleIntegerChange = (value) => {
    const data = isEmptyUtil(value) ? [] : [value];
    dispatch(setCurrentSelectedAnswer(data));
  };

  const handleOptionClick = (uid) => {
    if (isOptionDisabled()) return;

    if (currentQuestion.type === QUESTION_TYPE_MAP.SINGLE) {
      if (currentSelectedAnswer[0] === uid) {
        dispatch(setCurrentSelectedAnswer([]));
        return;
      }
      dispatch(setCurrentSelectedAnswer([uid]));
      return;
    }

    if (currentQuestion.type === QUESTION_TYPE_MAP.MCQ) {
      if (currentSelectedAnswer.includes(uid)) {
        dispatch(
          setCurrentSelectedAnswer(
            currentSelectedAnswer.filter((currentUID) => currentUID !== uid)
          )
        );
        return;
      }
      dispatch(setCurrentSelectedAnswer([...currentSelectedAnswer, uid]));
    }
  };

  if (currentQuestion.type === QUESTION_TYPE_MAP.INTEGER) {
    return (
      <NtaIntegerAnswer
        value={currentSelectedAnswer[0]}
        onChange={handleIntegerChange}
        disabled={isOptionDisabled()}
        allowNegative
        allowFloat
      />
    );
  }

  if (
    currentQuestion.type === QUESTION_TYPE_MAP.MCQ ||
    currentQuestion.type === QUESTION_TYPE_MAP.SINGLE
  ) {
    const hasComprehension = !!currentQuestion.comprehension?.[code];
    const disabled = isOptionDisabled();
    const controlType =
      currentQuestion.type === QUESTION_TYPE_MAP.SINGLE ? 'radio' : 'checkbox';

    return (
      <OptionsRow
        $hasComprehension={hasComprehension}
        data-image-preview
        data-testid="question-options"
      >
        {currentQuestion.answers?.map((answer) => {
          const isSelected = currentSelectedAnswer.includes(answer.uid);

          return (
            <OptionItem
              key={answer.uid}
              type="button"
              $hasComprehension={hasComprehension}
              $disabled={disabled}
              disabled={disabled}
              onClick={() => handleOptionClick(answer.uid)}
              aria-pressed={isSelected}
            >
              <NtaOptionControl type={controlType} checked={isSelected} />
              <OptionContent>
                <Dangerous
                  as={HTMLWrapper}
                  data-image-preview
                  $isImageClickable={false}
                  html={answer.content[code]}
                />
              </OptionContent>
            </OptionItem>
          );
        })}
      </OptionsRow>
    );
  }

  return null;
};

export default NtaOptions;

NtaOptions.propTypes = {
  code: PropTypes.number.isRequired
};
